import { useMemo } from 'react';
import { cx } from '@/utils';
import { useDotButton } from '@common/DotButton/DotButton';
import { TitleWrapper } from '@views/Home/components';
import useEmblaCarousel from 'embla-carousel-react';

import CustomLink from '@/components/common/CustomLink';
import { BlockColor } from '@/components/blocks/PageQuickAccessBar/admin/components/GeneralInformationQuickAccessBar';
import { splitTitle } from '@/components/views/Home/components/TitleWrapper';

import CarouselDots from '../../../common/CarouselDots';
import { QuickAccessContent, QuickAccessIcon } from './components';
import { CarouselNavigation, CarouselNavigationMobile } from './components/CarouselNavigation';
import styles from './styles.module.css';
import { chunkArray, getWrapperBackgroundColor, QuickAccessSharedProps } from './utils';

const DesktopStyleB = ({
  quickAccessBar,
  isCarousel,
  isLightBackground,
  itemsPerView,
  sortedItems,
}: QuickAccessSharedProps) => {
  const [emblaRefA, emblaApiA] = useEmblaCarousel({
    slidesToScroll: itemsPerView,
    containScroll: 'trimSnaps',
    breakpoints: {
      '(max-width: 767px)': {
        slidesToScroll: 1,
      },
    },
  });

  const {
    selectedIndex: selectedIndexA,
    scrollSnaps: scrollSnapsA,
    onDotButtonClick: onDotButtonClickA,
  } = useDotButton(emblaApiA);

  const [emblaRefB, emblaApiB] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const {
    selectedIndex: selectedIndexB,
    scrollSnaps: scrollSnapsB,
    onDotButtonClick: onDotButtonClickB,
  } = useDotButton(emblaApiB);

  const chunks = useMemo(() => chunkArray(sortedItems || [], 3), [sortedItems]);

  const [title1, title2] = splitTitle(quickAccessBar?.blockTitle, {
    show: quickAccessBar?.showTitle,
    defaultTitle1: 'Accesos',
    defaultTitle2: 'rápidos',
  });

  return (
    <section
      className={cx('pb-[20px] pt-6 md:pt-10', getWrapperBackgroundColor(quickAccessBar?.color))}
    >
      <div className={cx('inner-extend text-white', styles.titleWrapper)}>
        <TitleWrapper
          isDefault={!!quickAccessBar?.blockTitle}
          title1={title1}
          title2={title2}
          description={quickAccessBar?.showDescription ? quickAccessBar?.publicDescription : ''}
          action={
            quickAccessBar?.seeMoreTitle
              ? {
                  text: quickAccessBar.seeMoreTitle,
                  onClick: () => {
                    if (quickAccessBar?.seeMoreLink) {
                      window.open(quickAccessBar.seeMoreLink);
                    }
                  },
                }
              : undefined
          }
          classname={styles.titleName}
          title1ClassName={
            quickAccessBar?.color === BlockColor.WHITE || quickAccessBar?.color === BlockColor.GRAY
              ? 'text-primary text-xl font-semibold md:text-[32px]'
              : 'text-white text-xl font-semibold md:text-[32px]'
          }
          title2ClassName={
            quickAccessBar?.color === BlockColor.WHITE || quickAccessBar?.color === BlockColor.GRAY
              ? 'pl-1 font-montserrat text-xl md:text-[32px] text-tundora'
              : 'pl-1 font-montserrat text-xl md:text-[32px] text-white'
          }
          descriptionClassName={
            isLightBackground
              ? 'font-workSans text-base font-normal not-italic 3xl:text-lg text-tundora'
              : 'font-workSans text-base font-normal not-italic 3xl:text-lg text-white'
          }
          borderClassName={isLightBackground ? 'border-primary' : 'border-white'}
        />
      </div>
      <div className={'relative mt-[10px] overflow-hidden'}>
        {/* Botones de navegación solo si es carrusel */}
        {isCarousel && (
          <div className="hidden md:block">
            <CarouselNavigation
              onPrev={() => emblaApiA?.scrollPrev()}
              onNext={() => emblaApiA?.scrollNext()}
            />
          </div>
        )}

        {/* Contenedor principal */}
        <div className="inner-extend">
          {isCarousel ? (
            // Modo Carrusel
            <div className="overflow-hidden px-1 py-3" ref={emblaRefA}>
              <div className="flex gap-4 md:gap-[26px]">
                {sortedItems?.map((link, key) => (
                  <div
                    key={key}
                    className="w-[calc((100%-32px)/3)] min-w-0 flex-none md:w-[var(--desktop-width)] md:min-w-[279px]"
                    style={
                      {
                        '--desktop-width': `calc((100% - ${(itemsPerView - 1) * 26}px) / ${itemsPerView})`,
                      } as any
                    }
                  >
                    <CustomLink
                      className="flex h-full w-full flex-col items-center justify-start gap-2 rounded-xl bg-primaryShadcn-foreground p-2 opacity-100 shadow-md transition-opacity duration-150 hover:opacity-80 md:flex-row md:items-start md:justify-center md:gap-[22px] md:px-[24px] md:py-4"
                      pageLink={link.linkItem}
                    >
                      <QuickAccessIcon image={link?.image} />
                      <QuickAccessContent
                        title={link.quickAccessName}
                        description={link.description}
                        containerClassName="flex w-full flex-1 flex-col gap-[3px] self-center md:w-[168px]"
                        titleClassName="line-clamp-2 overflow-hidden text-center text-[14px] font-medium leading-[16px] md:font-bold text-primaryDark md:line-clamp-2 md:text-left md:text-[18px] md:leading-[20px]"
                        descriptionClassName="hidden line-clamp-3 text-justify text-[14px] leading-[15px] text-tundora md:block"
                      />
                    </CustomLink>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Modo Grid
            <>
              {/* Version Móvil: Carrusel de 3 items (Solo visible en Mobile) */}
              <div className="block pt-3 md:hidden">
                <div className="relative">
                  <CarouselNavigationMobile
                    onPrev={() => emblaApiB?.scrollPrev()}
                    onNext={() => emblaApiB?.scrollNext()}
                  />
                  <div className="mx-12 overflow-hidden pb-2" ref={emblaRefB}>
                    <div className="flex touch-pan-y gap-0">
                      {chunks.map((chunk, groupIndex) => (
                        <div
                          key={groupIndex}
                          className="flex min-w-0 flex-[0_0_100%] flex-col gap-4 pl-1 pr-1"
                        >
                          {chunk.map((link, index) => (
                            <CustomLink
                              key={`${groupIndex}-${index}`}
                              className="flex h-24 min-h-[80px] w-full flex-row items-center justify-center gap-2 rounded-[8px] border border-blueLight bg-white p-[10px] shadow-md"
                              pageLink={link.linkItem}
                            >
                              <QuickAccessIcon
                                image={link?.image}
                                className="[&_svg]:!h-[40px] [&_svg]:!w-[40px]"
                              />
                              <QuickAccessContent
                                title={link.quickAccessName}
                                description={link.description}
                                containerClassName="flex min-w-0 flex-1 flex-col gap-1"
                                titleClassName="line-clamp-2 overflow-hidden text-left text-[14px] font-bold leading-[16px] text-primaryDark"
                                descriptionClassName="line-clamp-3 text-[12px] leading-[14px] text-tundora"
                              />
                            </CustomLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Dots Mobile para Grid Mode */}
                <CarouselDots
                  scrollSnaps={scrollSnapsB}
                  selectedIndex={selectedIndexB}
                  onDotClick={onDotButtonClickB}
                  dotClassName={cx('dot', isLightBackground ? styles.dotLight : undefined)}
                  selectedClassName={
                    isLightBackground ? styles.dotLightSelected : styles.dotDarkSelected
                  }
                />
              </div>

              {/* Versión Escritorio: Grid (sin cambios) */}
              <div className="hidden grid-cols-[repeat(auto-fill,minmax(375px,1fr))] gap-[26px] pb-2 pt-3 md:grid">
                {sortedItems?.map((link, key) => (
                  <CustomLink
                    key={key}
                    className="flex h-[107px] w-full flex-row items-start justify-center gap-[22px] rounded-xl border-[1px] border-blueLight bg-white px-[24px] pb-4 pt-4 opacity-100 shadow-md transition-opacity duration-150 hover:opacity-80"
                    pageLink={link.linkItem}
                  >
                    <QuickAccessIcon
                      image={link?.image}
                      className="[&_svg]:!h-[40px] [&_svg]:!w-[40px] md:[&_svg]:!h-[42px] md:[&_svg]:!w-[42px]"
                    />
                    <QuickAccessContent
                      title={link.quickAccessName}
                      description={link.description}
                      titleClassName="line-clamp-2 overflow-hidden text-left text-[14px] font-bold leading-[16px] text-primaryDark md:text-[18px] md:leading-[20px]"
                      descriptionClassName="line-clamp-3 text-[12px] leading-[14px] text-tundora md:text-[14px] md:leading-[15px]"
                    />
                  </CustomLink>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dots solo si es carrusel */}
      {isCarousel && (
        <CarouselDots
          scrollSnaps={scrollSnapsA}
          selectedIndex={selectedIndexA}
          onDotClick={onDotButtonClickA}
          dotClassName={cx('dot', isLightBackground ? styles.dotLight : undefined)}
          selectedClassName={isLightBackground ? styles.dotLightSelected : styles.dotDarkSelected}
        />
      )}
    </section>
  );
};

export default DesktopStyleB;
